import { Party } from '../models';
import { isPartySeated } from './is-party-seated';

/**
 * The set of party fields that affect availability / work-time validation.
 * When none of these change on an existing party, an edit (e.g. adding a note)
 * must not be re-validated against current availability or business hours.
 */
export interface PartyCriticalFields {
  size: number;
  duration: number;
  quotedTime: number;
  services: string[];
  resources: string[];
  seatedTime: number | null;
  checkinTime: number | null;
}

/**
 * Order-insensitive equality for id arrays. Exported so a component can check whether a single
 * field (e.g. just `services`) changed, rather than the whole PartyCriticalFields snapshot.
 */
export function partyIdsEqual(a: string[] = [], b: string[] = []): boolean {
  return a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',');
}

/**
 * Compare two critical-field snapshots for equality. Order-insensitive for the
 * `services`/`resources` id arrays, and tolerant of `undefined` vs `null` for the
 * optional state fields (`seatedTime`/`checkinTime`).
 */
export function partyCriticalFieldsEqual(a: PartyCriticalFields, b: PartyCriticalFields): boolean {
  const nz = (v: number | null | undefined): number | null => v ?? null;

  return a.size === b.size
    && nz(a.duration) === nz(b.duration)
    && a.quotedTime === b.quotedTime
    && nz(a.seatedTime) === nz(b.seatedTime)
    && nz(a.checkinTime) === nz(b.checkinTime)
    && partyIdsEqual(a.services, b.services)
    && partyIdsEqual(a.resources, b.resources);
}

/**
 * Whether the current critical-field snapshot matches the one captured when the
 * existing party was opened for editing. `current` is a factory so the (component-
 * specific) snapshot is only built when there is an `original` to compare against —
 * i.e. never in add mode, where reading party state may be unsafe.
 */
export function partyCriticalFieldsUnchanged(
  original: PartyCriticalFields | null | undefined,
  current: () => PartyCriticalFields,
): boolean {
  return !!original && partyCriticalFieldsEqual(current(), original);
}

/**
 * Whether the new-party availability/work-time validation cascade should be bypassed.
 * It is irrelevant when editing an existing party that is (a) already seated, or
 * (b) has no availability-affecting field changed — in both cases the party was valid
 * when saved and must stay saveable. Shared by all party add/edit components.
 */
export function canBypassNewPartyValidation(
  isEditMode: boolean,
  partyToEdit: Pick<Party, 'seated_time'> | null | undefined,
  original: PartyCriticalFields | null | undefined,
  current: () => PartyCriticalFields,
): boolean {
  if (!isEditMode) {
    return false;
  }

  return isPartySeated(partyToEdit) || partyCriticalFieldsUnchanged(original, current);
}
